import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '../../../layouts/PageHeaderExtra';
import { useStatoNelTitolo } from '../../../components/Util/StatoProdotto';
import {
    aggiornaPaginaApp,
    caricaImmaginePagina,
    cronologiaPaginaApp,
    elencoImmaginiPagine,
    leggiPaginaApp,
    urlImmaginePagina,
} from '../../../../api-client/appPlayer';
import { LIST_URL } from './index';

/*
 * Scheda di una pagina dell'app: HTML e CSS, le immagini che le pagine usano e le versioni precedenti.
 * A destra l'anteprima come nel telefono, con o senza Premium. Le modifiche arrivano nell'app entro un
 * minuto; il server toglie script, eventi e link non ammessi. Le regole per chi scrive le pagine sono nel
 * documento degli interventi (cap. 1, «Come sono fatte le pagine»).
 *
 * Il riquadro della Dashboard (`dashboard-premium`) si puo` spegnere: lo stato sta accanto al titolo.
 */

const SPEGNIBILI = ['dashboard-premium'];
const FONT = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Bebas+Neue&display=swap';

/** Lo stesso documento che l'app costruisce attorno alla pagina, per l'anteprima. */
const documentoAnteprima = (html, css, premium) => `<!doctype html><html><head>
<meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<base href="${urlImmaginePagina('')}">
<link rel="stylesheet" href="${FONT}">
<style>:root{--spazio-alto:44px;--spazio-basso:34px}body.premium .solo-free,body.free .solo-premium{display:none!important}</style>
<style>${String(css || '').replace(/<\//g, '')}</style>
</head><body class="${premium ? 'premium' : 'free'}">${html || ''}</body></html>`;

const dataOra = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

export const SchedaPaginaApp = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [pagina, setPagina] = useState(null);
    const [form, setForm] = useState({ title: '', html: '', css: '' });
    const [immagini, setImmagini] = useState([]);
    const [versioni, setVersioni] = useState([]);
    const [premium, setPremium] = useState(false);
    const [errore, setErrore] = useState('');
    const [inCorso, setInCorso] = useState(false);
    const [salvato, setSalvato] = useState(false);
    const [copiato, setCopiato] = useState('');
    const fileImmagine = useRef(null);

    const caricaVersioni = () => cronologiaPaginaApp(slug).then((r) => setVersioni(r?.error ? [] : r));

    useEffect(() => {
        leggiPaginaApp(slug).then((r) => {
            if (!r || r.error) { setErrore(r?.error || 'Pagina non trovata.'); setPagina({}); return; }
            setPagina(r);
            setForm({ title: r.title || '', html: r.html || '', css: r.css || '' });
        });
        elencoImmaginiPagine().then((r) => setImmagini(r?.error ? [] : r));
        caricaVersioni();
    }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

    usePageTitle(pagina?.title);
    useEffect(() => {
        if (!salvato) return undefined;
        const t = setTimeout(() => setSalvato(false), 2500);
        return () => clearTimeout(t);
    }, [salvato]);

    useStatoNelTitolo({
        visibile: !!pagina?.slug && SPEGNIBILI.includes(slug),
        attivo: pagina?.active !== false,
        onCambia: async (attivo) => {
            const r = await aggiornaPaginaApp(slug, { active: attivo });
            if (r?.error) return { error: r.error };
            setPagina(r);
            setSalvato(true);
            caricaVersioni();
            return {};
        },
    });

    const anteprima = useMemo(() => documentoAnteprima(form.html, form.css, premium), [form.html, form.css, premium]);
    const cambiato = pagina && (form.title !== (pagina.title || '') || form.html !== (pagina.html || '') || form.css !== (pagina.css || ''));

    const salva = async () => {
        if (!form.title.trim()) { setErrore('Scrivi il nome della pagina.'); return; }
        setInCorso(true);
        const r = await aggiornaPaginaApp(slug, { title: form.title.trim(), html: form.html, css: form.css });
        setInCorso(false);
        if (r?.error) { setErrore(r.error); return; }
        setPagina(r);
        // Il server toglie quello che non e` ammesso: nell'editor resta la versione salvata davvero
        setForm({ title: r.title || '', html: r.html || '', css: r.css || '' });
        setSalvato(true);
        caricaVersioni();
    };

    const caricaImmagine = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setInCorso(true);
        const r = await caricaImmaginePagina(file);
        setInCorso(false);
        if (r?.error) { setErrore(r.error); return; }
        setImmagini((lista) => [...lista.filter((i) => i.fileName !== r.fileName), r].sort((a, b) => a.fileName.localeCompare(b.fileName)));
    };

    const copia = async (testo) => {
        try { await navigator.clipboard.writeText(testo); setCopiato(testo); setTimeout(() => setCopiato(''), 2000); } catch { /* niente appunti */ }
    };

    if (!pagina) return <div className="text-center py-5"><Spinner animation="border" role="status" /></div>;

    return (
        <>
            <Modal show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Controlla i dati</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
            </Modal>

            <Row className="g-4">
                <Col xl={7}>
                    <Card>
                        <Card.Body className="sid-card-body--spaced">
                            <Form.Label htmlFor="pa-titolo">Nome della pagina</Form.Label>
                            <Form.Control id="pa-titolo" value={form.title} maxLength={120} onChange={(e) => setForm({ ...form, title: e.target.value })} />

                            <Form.Label htmlFor="pa-html" className="mt-4">HTML</Form.Label>
                            <Form.Control as="textarea" id="pa-html" className="sid-codice" rows={16} spellCheck={false} maxLength={50000}
                                value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} />
                            <Form.Text className="text-dark">
                                Solo il contenuto del body. Link: sportid://premium, sportid://coachbot, sportid://acquista, sportid://chiudi
                                o https. Classi solo-premium e solo-free per chi ha o non ha Premium.
                            </Form.Text>

                            <Form.Label htmlFor="pa-css" className="mt-4">CSS</Form.Label>
                            <Form.Control as="textarea" id="pa-css" className="sid-codice" rows={12} spellCheck={false} maxLength={30000}
                                value={form.css} onChange={(e) => setForm({ ...form, css: e.target.value })} />
                            <Form.Text className="text-dark">Caratteri Inter e Bebas Neue; spazi delle barre del telefono in var(--spazio-alto) e var(--spazio-basso).</Form.Text>

                            <h5 className="sid-form-section">Immagini</h5>
                            <p className="sid-nota-leggibile">
                                Nelle pagine si scrivono con il percorso <strong>appPages/images/nome</strong>. JPG, PNG o WEBP fino a 2 MB;
                                un'immagine con lo stesso nome sostituisce quella di prima, in tutte le pagine.
                            </p>
                            <div className="sid-immagini-pagine">
                                {immagini.map((i) => (
                                    <div key={i.fileName} className="sid-immagini-pagine__voce">
                                        <img src={i.url || urlImmaginePagina(i.src)} alt="" />
                                        <div className="sid-immagini-pagine__nome">{i.src}</div>
                                        <Button type="button" className="btn btn-dark light btn-sm" onClick={() => copia(i.src)}>
                                            {copiato === i.src ? 'Copiato' : 'Copia percorso'}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                            <input ref={fileImmagine} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={caricaImmagine} />
                            <Button type="button" className="btn btn-light mt-3" onClick={() => fileImmagine.current?.click()} disabled={inCorso}>Carica immagine</Button>

                            <h5 className="sid-form-section">Versioni precedenti</h5>
                            {versioni.length === 0 && <p>Nessuna modifica salvata finora.</p>}
                            {versioni.map((v) => (
                                <div key={v.savedAt} className="d-flex align-items-center gap-3 py-2 sid-riga-versione">
                                    <span>{dataOra(v.updatedAt)}</span>
                                    <span className="ms-auto" />
                                    <Button type="button" className="btn btn-dark light btn-sm"
                                        onClick={() => setForm({ title: v.title || form.title, html: v.html || '', css: v.css || '' })}>
                                        Rimetti nell'editor
                                    </Button>
                                </div>
                            ))}
                            {versioni.length > 0 && <Form.Text className="text-dark">«Rimetti nell'editor» carica quella versione: diventa la pagina dell'app solo con Salva.</Form.Text>}
                        </Card.Body>
                    </Card>
                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button className="btn btn-dark light" onClick={() => navigate(LIST_URL)}>Indietro</Button>
                        <Button className="btn btn-primary" onClick={salva} disabled={inCorso || !cambiato}>Salva</Button>
                    </div>
                </Col>
                <Col xl={5}>
                    <div className="sid-telefono-anteprima">
                        <div className="d-flex align-items-center gap-3 mb-2">
                            <strong>Anteprima</strong>
                            <Form.Check type="switch" id="pa-premium" label="Con Premium" checked={premium} onChange={(e) => setPremium(e.target.checked)} />
                        </div>
                        <iframe title="Anteprima della pagina" sandbox="allow-same-origin" srcDoc={anteprima} className="sid-telefono-anteprima__schermo" />
                    </div>
                </Col>
            </Row>
            {salvato && <div className="sid-avviso-salvato" role="status">✓ Modifica salvata</div>}
        </>
    );
};

export default SchedaPaginaApp;
